
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { UserCreditPackageManager } from "@/components/devices/UserCreditPackageManager";
import { CreditAuditTrail } from "@/components/devices/CreditAuditTrail";
import { CreditAnalyticsDashboard } from "@/components/devices/CreditAnalyticsDashboard";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useCreditAllocations } from "@/hooks/use-enhanced-credit-packages";
import {
  useCreditPackagesWithDevices,
  formatDeviceLabel,
  formatDeviceSubLabel,
  type EnrichedCreditPackage,
} from "@/hooks/use-credit-packages-with-devices";
import { useFleetModeQuota } from "@/hooks/use-fleet-mode-quota";
import { Input } from "@/components/ui/input";
import { FleetPoolPanel } from "@/components/devices/FleetPoolPanel";
import { CreditManagementDevicesTab } from "@/components/devices/CreditManagementDevicesTab";
import { useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  Package, 
  TrendingUp, 
  Users, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Shield,
  User,
  Camera,
  Search,
  Layers
} from "lucide-react";
import { format } from "date-fns";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";

const DevicePackagesSection = ({ packages, orderBasedPackages, manualPackages, search, onSearchChange }: {
  packages: EnrichedCreditPackage[];
  orderBasedPackages: EnrichedCreditPackage[];
  manualPackages: EnrichedCreditPackage[];
  search: string;
  onSearchChange: (v: string) => void;
}) => {
  const q = search.trim().toLowerCase();
  const matchesSearch = (pkg: EnrichedCreditPackage) => {
    if (!q) return true;
    return (
      (pkg.device_name ?? "").toLowerCase().includes(q) ||
      (pkg.device_serial ?? "").toLowerCase().includes(q) ||
      (pkg.device_id ?? "").toLowerCase().includes(q) ||
      (pkg.package_name ?? "").toLowerCase().includes(q) ||
      (pkg.purchaser_user_id ?? "").toLowerCase().includes(q)
    );
  };
  const deviceSpecificPackages = packages.filter(pkg => pkg.device_id !== 'USER_OWNED' && matchesSearch(pkg));
  const availablePackages = deviceSpecificPackages.filter(pkg => !pkg.is_assigned && pkg.remaining_credits > 0);
  const assignedPackages = deviceSpecificPackages.filter(pkg => pkg.is_assigned);
  const depletedPackages = deviceSpecificPackages.filter(pkg => pkg.remaining_credits === 0);

  const getAssignmentTypeColor = (type: string) => {
    switch (type) {
      case 'order_based': return 'bg-green-100 text-green-800';
      case 'admin_override': return 'bg-purple-100 text-purple-800';
      case 'user_owned': return 'bg-purple-100 text-purple-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusColor = (isAssigned: boolean, remainingCredits: number) => {
    if (remainingCredits === 0) return 'bg-gray-100 text-gray-800';
    if (isAssigned) return 'bg-green-100 text-green-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getStatusText = (isAssigned: boolean, remainingCredits: number) => {
    if (remainingCredits === 0) return 'depleted';
    if (isAssigned) return 'assigned';
    return 'available';
  };

  const CreditDisplay = ({ pkg }: { pkg: any }) => {
    if (pkg.order_info?.total_cameras > 0) {
      return (
        <div className="text-sm font-medium">
          <div className="flex items-center gap-1">
            <span>{pkg.remaining_credits} / {pkg.total_credits} credits</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Camera className="h-3 w-3" />
            <span>{pkg.order_info.total_cameras} cameras @ {pkg.order_info.credits_per_camera} each</span>
          </div>
        </div>
      );
    }
    
    return (
      <div className="text-sm font-medium">
        {pkg.remaining_credits} / {pkg.total_credits} credits
      </div>
    );
  };

  const DeviceLine = ({ pkg }: { pkg: EnrichedCreditPackage }) => (
    <>
      <div className="text-sm">
        <span className="font-medium">{formatDeviceLabel(pkg)}</span>
      </div>
      {formatDeviceSubLabel(pkg) && (
        <div className="text-xs text-muted-foreground">{formatDeviceSubLabel(pkg)}</div>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Package className="h-5 w-5" />
          Used Credit Packages
        </h3>
        <Badge variant="outline">
          {deviceSpecificPackages.length} packages in use
        </Badge>
      </div>
      
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-10"
          placeholder="Search by device name, serial, ID, package or purchaser…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <div className="space-y-4">
      {/* Available Packages */}
      {availablePackages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-4 w-4 text-blue-600" />
              Available Credit Packages ({availablePackages.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {availablePackages.map((pkg) => (
                <div key={pkg.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="space-y-1">
                    <div className="font-medium">{pkg.package_name}</div>
                    <DeviceLine pkg={pkg} />
                    {pkg.purchaser_user_id && (
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Purchaser: {pkg.purchaser_user_id}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      Created: {format(new Date(pkg.created_at), 'MMM dd, yyyy')}
                    </div>
                  </div>
                  <div className="text-right space-y-2">
                    <div className="flex gap-2">
                      <Badge className={getAssignmentTypeColor(pkg.assignment_type)}>
                        {pkg.assignment_type.replace('_', ' ')}
                      </Badge>
                      <Badge className={getStatusColor(pkg.is_assigned, pkg.remaining_credits)}>
                        {getStatusText(pkg.is_assigned, pkg.remaining_credits)}
                      </Badge>
                    </div>
                    <CreditDisplay pkg={pkg} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assigned Packages */}
      {assignedPackages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Assigned Credit Packages ({assignedPackages.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {assignedPackages.map((pkg) => (
                <div key={pkg.id} className="flex items-center justify-between p-3 border rounded-lg bg-green-50">
                  <div className="space-y-1">
                    <div className="font-medium">{pkg.package_name}</div>
                    <DeviceLine pkg={pkg} />
                    {pkg.purchaser_user_id && (
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Purchaser: {pkg.purchaser_user_id}
                      </div>
                    )}
                  </div>
                  <div className="text-right space-y-1">
                    <Badge className={getAssignmentTypeColor(pkg.assignment_type)}>
                      {pkg.assignment_type.replace('_', ' ')}
                    </Badge>
                    <CreditDisplay pkg={pkg} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Depleted Packages */}
      {depletedPackages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-600" />
              Depleted Credit Packages ({depletedPackages.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {depletedPackages.map((pkg) => (
                <div key={pkg.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                  <div className="space-y-1">
                    <div className="font-medium text-gray-700">{pkg.package_name}</div>
                    <DeviceLine pkg={pkg} />
                  </div>
                  <div className="text-right space-y-1">
                    <Badge className="bg-gray-100 text-gray-800">depleted</Badge>
                    <div className="text-sm font-medium text-gray-600">
                      {pkg.order_info?.total_cameras > 0 ? (
                        <div className="flex items-center gap-1">
                          <Camera className="h-3 w-3" />
                          <span>{pkg.order_info.total_cameras} cameras used</span>
                        </div>
                      ) : (
                        <span>{pkg.total_credits} credits used</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {deviceSpecificPackages.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {q
                ? "No packages match the current search."
                : "No device-specific credit packages found."}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Create a package or complete an order to see packages here.
            </p>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
};

const EnhancedCreditManagement = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  
  // Get user's organization ID
  const { data: userOrganization, isLoading: isLoadingOrganization } = useQuery({
    queryKey: ['user-organization', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching user organization:', error);
        return null;
      }
      
      console.log('User organization data:', { userId: user.id, organizationId: data?.organization_id });
      return data?.organization_id || null;
    },
    enabled: !!user?.id,
  });
  
  const {
    data: allPackagesUnfiltered = [],
    isLoading: isLoadingPackages,
    deviceLookup,
  } = useCreditPackagesWithDevices();
  const { data: allAllocations = [] } = useCreditAllocations();
  const fleet = useFleetModeQuota(userOrganization);

  // Filter packages by user's organization ID - only show packages if user has organization
  const allPackages = allPackagesUnfiltered.filter(pkg => {
    return userOrganization && pkg.organization_id === userOrganization;
  });

  // Calculate enhanced statistics
  const totalCredits = allPackages.reduce((sum, pkg) => sum + pkg.total_credits, 0);
  const totalRemaining = allPackages.reduce((sum, pkg) => sum + pkg.remaining_credits, 0);
  const totalAssigned = allPackages.reduce((sum, pkg) => sum + pkg.assigned_credits, 0);
  
  const orderBasedPackages = allPackages.filter(pkg => pkg.assignment_type === 'order_based');
  const manualPackages = allPackages.filter(pkg => pkg.assignment_type === 'manual');
  const unassignedPackages = allPackages.filter(pkg => !pkg.is_assigned && pkg.remaining_credits > 0);
  
  const conflictAllocations = allAllocations.filter(allocation => 
    // This would need to be enhanced with conflict detection logic
    false // Placeholder - would check against audit table
  );

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader
            {...PAGE_HEADERS.creditManagement}
            actions={
              <div className="flex items-center gap-2">
                {fleet.isFleetMode && (
                  <Badge className="bg-teal-100 text-teal-800 border-teal-200">
                    <Layers className="h-3 w-3 mr-1" /> Fleet Mode
                  </Badge>
                )}
                <Badge variant="outline" className="text-sm">
                  {allPackages.length} total packages
                </Badge>
              </div>
            }
          />
          <div className="p-8 space-y-8" style={{ paddingTop: "8.5rem" }}>

            {/* Enhanced Statistics Dashboard */}
            <div className={`grid grid-cols-1 ${fleet.isFleetMode ? "md:grid-cols-7" : "md:grid-cols-6"} gap-4`}>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Credits</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{totalCredits.toLocaleString()}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Available</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{totalRemaining.toLocaleString()}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Assigned</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">{totalAssigned.toLocaleString()}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Order-Based</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{orderBasedPackages.length}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Manual</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">{manualPackages.length}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Utilization</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {totalCredits > 0 ? Math.round((totalAssigned / totalCredits) * 100) : 0}%
                  </div>
                </CardContent>
              </Card>
              {fleet.isFleetMode && (
                <Card className="border-teal-300 bg-teal-50/40">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-teal-800 flex items-center gap-1">
                      <Layers className="h-3 w-3" /> Fleet Pool
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {fleet.totalUsed.toLocaleString()}
                      <span className="text-sm font-normal text-muted-foreground">
                        {" "}/ {fleet.totalAllowed.toLocaleString()}
                      </span>
                    </div>
                    <Progress
                      value={
                        fleet.totalAllowed > 0
                          ? Math.min(100, Math.round((fleet.totalUsed / fleet.totalAllowed) * 100))
                          : 0
                      }
                      className="mt-2"
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            {/* System Status Alerts */}
            {unassignedPackages.length > 0 && (
              <Alert>
                <Package className="h-4 w-4" />
                <AlertDescription>
                  You have {unassignedPackages.length} unassigned credit packages with available credits. 
                  Use the Combined Assignment tab to assign them to users.
                </AlertDescription>
              </Alert>
            )}

            <Tabs defaultValue="user-credits" className="space-y-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="user-credits" className="data-[state=active]:bg-[#DFEF97] data-[state=active]:text-black">Available</TabsTrigger>
                <TabsTrigger value="device-packages" className="data-[state=active]:bg-[#DFEF97] data-[state=active]:text-black">Used</TabsTrigger>
                <TabsTrigger value="devices" className="data-[state=active]:bg-[#DFEF97] data-[state=active]:text-black">Devices</TabsTrigger>
                <TabsTrigger value="analytics" className="data-[state=active]:bg-[#DFEF97] data-[state=active]:text-black">Analytics</TabsTrigger>
              </TabsList>

        <TabsContent value="user-credits" className="space-y-4">
          <UserCreditPackageManager packages={allPackages} />
        </TabsContent>

              <TabsContent value="device-packages" className="space-y-4">
                {fleet.isFleetMode && (
                  <FleetPoolPanel
                    organizationId={userOrganization}
                    deviceLookup={deviceLookup}
                  />
                )}
                <DevicePackagesSection 
                  packages={allPackages} 
                  orderBasedPackages={orderBasedPackages}
                  manualPackages={manualPackages}
                  search={searchTerm}
                  onSearchChange={setSearchTerm}
                />
                
                {/* Credit Allocation Audit Trail */}
                <CreditAuditTrail />
              </TabsContent>

              <TabsContent value="devices" className="space-y-4">
                {fleet.isFleetMode && (
                  <FleetPoolPanel
                    organizationId={userOrganization}
                    deviceLookup={deviceLookup}
                  />
                )}
                <CreditManagementDevicesTab packages={allPackages} />
              </TabsContent>

              <TabsContent value="analytics" className="space-y-4">
                <CreditAnalyticsDashboard organizationId={userOrganization} />
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default EnhancedCreditManagement;
